// F-065 : carnet d'entretien et garanties, par logement.
import { useState } from "react";
import { post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, money } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, Modal, MoneyInput, PageHeader, Select, Table, Textarea, fieldError, useToast } from "@core/ui";
import { EQUIPMENT_KINDS, MAINTAINERS, MAINTENANCE_EVENTS, OverdueBadge, PropertySelect, RuleNote, meLease, options, orgApi, todayIso, useAction } from "./shared";

function Flags({ f }: { f: any }) {
  return (
    <span className="row">
      {f.maintenance_overdue ? <OverdueBadge /> : null}
      {f.maintenance_due_soon ? <Badge tone="attention">Entretien à prévoir</Badge> : null}
      {f.warranty_ends_soon ? <Badge tone="attention">Fin de garantie proche</Badge> : null}
      {f.warranty_active === true ? <Badge tone="succes">Sous garantie</Badge> : null}
    </span>
  );
}

function EquipmentForm({ orgId, propertyId, onDone }: { orgId: string; propertyId?: string; onDone: () => void }) {
  const [form, setForm] = useState({ property_id: propertyId ?? "", kind: "chaudiere", name: "", brand: "", model: "", installed_on: "", warranty_until: "", maintenance_interval_months: "12", last_maintenance_on: "", maintenance_by: "a_preciser", notes: "" });
  const act = useAction();
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const payload = {
          ...form,
          installed_on: form.installed_on || undefined,
          warranty_until: form.warranty_until || undefined,
          last_maintenance_on: form.last_maintenance_on || undefined,
          maintenance_interval_months: form.maintenance_interval_months ? Number(form.maintenance_interval_months) : undefined,
        };
        if (await act.run(() => post(orgApi(orgId, "/equipments"), payload), "Équipement ajouté.")) onDone();
      }}
    >
      <ErrorBox error={act.error} />
      {!propertyId ? <PropertySelect orgId={orgId} value={form.property_id} error={fieldError(act.error, "property_id")} onChange={(v) => setForm({ ...form, property_id: v })} /> : null}
      <div className="form-row">
        <Select label="Type" value={form.kind} options={options(EQUIPMENT_KINDS)} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
        <Input label="Nom" required value={form.name} error={fieldError(act.error, "name")} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="form-row">
        <Input label="Marque" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
        <Input label="Modèle" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
      </div>
      <div className="form-row">
        <Input label="Date de pose" type="date" value={form.installed_on} onChange={(e) => setForm({ ...form, installed_on: e.target.value })} />
        <Input label="Fin de garantie" type="date" value={form.warranty_until} error={fieldError(act.error, "warranty_until")} onChange={(e) => setForm({ ...form, warranty_until: e.target.value })} />
      </div>
      <div className="form-row">
        <Input label="Entretien tous les (mois)" type="number" min={1} max={120} value={form.maintenance_interval_months} error={fieldError(act.error, "maintenance_interval_months")} onChange={(e) => setForm({ ...form, maintenance_interval_months: e.target.value })} />
        <Input label="Dernier entretien" type="date" value={form.last_maintenance_on} onChange={(e) => setForm({ ...form, last_maintenance_on: e.target.value })} />
        <Select label="Entretien à la charge de" value={form.maintenance_by} options={options(MAINTAINERS)} onChange={(e) => setForm({ ...form, maintenance_by: e.target.value })} />
      </div>
      <Textarea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      <div>
        <Button type="submit" busy={act.busy}>
          Ajouter l’équipement
        </Button>
      </div>
    </form>
  );
}

function EquipmentDetail({ orgId, id, onClose }: { orgId: string; id: string; onClose: () => void }) {
  const org = useOrg();
  const path = orgApi(orgId, `/equipments/${id}`);
  const q = useGet<any>(path);
  const invalidate = useInvalidate();
  const [ev, setEv] = useState({ kind: "entretien", status: "realise", event_on: todayIso(), contractor: "", cost_cents: null as number | null, notes: "" });
  const act = useAction();
  const toast = useToast();
  const e = q.data?.equipment;
  return (
    <Modal open onClose={onClose} title={e ? e.name : "Équipement"}>
      {!q.data ? (
        <Loading />
      ) : (
        <div className="stack">
          <ErrorBox error={act.error} />
          <p className="small muted">
            {EQUIPMENT_KINDS[e.kind]} · {e.property_label} · pose {date(e.installed_on)} · garantie jusqu’au {date(e.warranty_until)} · prochain entretien {date(e.next_maintenance_on)}
          </p>
          <Flags f={e.flags} />
          <RuleNote>{q.data.rappel}</RuleNote>
          {org.can("operate") ? (
            <Button
              small
              variant="secondaire"
              onClick={async () => {
                const r = await act.run(() => post<{ created: boolean }>(`${path}/reminder`, {}));
                if (r) toast(r.created ? "Rappel ajouté à l’agenda." : "Un rappel est déjà programmé pour cet entretien.", r.created ? "succes" : "info");
                invalidate(orgApi(orgId, "/agenda"));
              }}
            >
              Programmer un rappel dans l’agenda
            </Button>
          ) : null}
          <h3>Historique</h3>
          {q.data.events.length ? (
            <Table caption="Opérations">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Opération</th>
                  <th>Intervenant</th>
                  <th className="right">Coût</th>
                </tr>
              </thead>
              <tbody>
                {q.data.events.map((x: any) => (
                  <tr key={x.id}>
                    <td className="num">{date(x.event_on)}</td>
                    <td>
                      {MAINTENANCE_EVENTS[x.kind]} {x.status === "prevu" ? <Badge>Prévu</Badge> : null}
                      {x.notes ? <span className="block small muted">{x.notes}</span> : null}
                    </td>
                    <td>{x.contractor}</td>
                    <td className="vie-amount">{x.cost_cents != null ? money(x.cost_cents) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="muted">Aucune opération enregistrée.</p>
          )}
          {org.can("operate") ? (
            <form
              className="stack-sm"
              onSubmit={async (x) => {
                x.preventDefault();
                if (await act.run(() => post(`${path}/events`, { ...ev, cost_cents: ev.cost_cents ?? undefined }), "Opération ajoutée.")) {
                  invalidate(path, orgApi(orgId, "/equipments"));
                  setEv({ ...ev, contractor: "", cost_cents: null, notes: "" });
                }
              }}
            >
              <h3>Ajouter une opération</h3>
              <div className="form-row">
                <Select label="Opération" value={ev.kind} options={options(MAINTENANCE_EVENTS)} onChange={(x) => setEv({ ...ev, kind: x.target.value })} />
                <Select label="État" value={ev.status} options={[{ value: "realise", label: "Réalisée" }, { value: "prevu", label: "Prévue" }]} onChange={(x) => setEv({ ...ev, status: x.target.value })} />
                <Input label="Date" type="date" value={ev.event_on} error={fieldError(act.error, "event_on")} onChange={(x) => setEv({ ...ev, event_on: x.target.value })} />
              </div>
              <div className="form-row">
                <Input label="Intervenant" value={ev.contractor} onChange={(x) => setEv({ ...ev, contractor: x.target.value })} />
                <MoneyInput label="Coût" cents={ev.cost_cents} onChange={(c) => setEv({ ...ev, cost_cents: c })} />
              </div>
              <Input label="Notes" value={ev.notes} onChange={(x) => setEv({ ...ev, notes: x.target.value })} />
              <div>
                <Button type="submit" variant="secondaire" busy={act.busy}>
                  Ajouter
                </Button>
              </div>
            </form>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function EquipmentTable({ orgId, propertyId }: { orgId: string; propertyId?: string }) {
  const org = useOrg();
  const path = orgApi(orgId, `/equipments${propertyId ? `?property_id=${propertyId}` : ""}`);
  const q = useGet<{ items: any[] }>(path);
  const invalidate = useInvalidate();
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState("");
  return (
    <div className="stack">
      <div className="row-between">
        <p className="muted">L’historique reste attaché au logement, quel que soit l’occupant. Une garantie expirée ne préjuge pas d’autres droits.</p>
        {org.can("operate") ? (
          <Button variant="secondaire" onClick={() => setAdding(!adding)}>
            {adding ? "Fermer" : "Ajouter un équipement"}
          </Button>
        ) : null}
      </div>
      {adding ? (
        <Card title="Nouvel équipement">
          <EquipmentForm
            orgId={orgId}
            propertyId={propertyId}
            onDone={() => {
              setAdding(false);
              invalidate(orgApi(orgId, "/equipments"));
            }}
          />
        </Card>
      ) : null}
      {q.isLoading ? (
        <Loading />
      ) : q.data?.items.length ? (
        <Table caption="Équipements">
          <thead>
            <tr>
              <th>Équipement</th>
              {!propertyId ? <th>Logement</th> : null}
              <th>Prochain entretien</th>
              <th>Garantie</th>
              <th>À la charge de</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((e) => (
              <tr key={e.id}>
                <td>
                  <button type="button" className="vie-link" onClick={() => setOpen(e.id)}>
                    {e.name}
                  </button>
                  <span className="block small muted">{EQUIPMENT_KINDS[e.kind]}</span>
                  <Flags f={e.flags} />
                </td>
                {!propertyId ? <td>{e.property_label}</td> : null}
                <td className="num">{date(e.next_maintenance_on)}</td>
                <td className="num">{date(e.warranty_until)}</td>
                <td>{MAINTAINERS[e.maintenance_by]}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Empty title="Aucun équipement">Ajoutez la chaudière, le chauffe-eau ou la VMC pour suivre entretiens et garanties.</Empty>
      )}
      {open ? <EquipmentDetail orgId={orgId} id={open} onClose={() => setOpen("")} /> : null}
    </div>
  );
}

export function MaintenancePage() {
  const org = useOrg();
  return (
    <div className="page">
      <PageHeader eyebrow="Vie du logement" title="Entretien et garanties" intro="Équipements par logement, entretiens réalisés et prévus, fins de garantie. Les échéances apparaissent dans l’agenda." />
      <EquipmentTable orgId={org.orgId} />
    </div>
  );
}

/** Onglet « Entretien » de la fiche logement. */
export function PropertyMaintenanceTab({ orgId, propertyId }: { orgId: string; propertyId: string }) {
  return <EquipmentTable orgId={orgId} propertyId={propertyId} />;
}

/** Équipements du logement loué, vus par le locataire. */
export function TenantEquipments({ leaseId }: { leaseId: string }) {
  const q = useGet<{ items: any[] }>(meLease(leaseId, "/equipments"));
  if (!q.data?.items.length) return null;
  return (
    <Card title="Équipements du logement">
      <ul className="list-plain">
        {q.data.items.map((e) => (
          <li key={e.id}>
            <strong>{e.name}</strong> <span className="small muted">· {EQUIPMENT_KINDS[e.kind]}</span>
            <span className="block small">
              Prochain entretien : {date(e.next_maintenance_on)} · à la charge de : {MAINTAINERS[e.maintenance_by].toLowerCase()}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
